import React, { useState, useEffect, Suspense, lazy } from 'react';
import { PropertyProvider, useProperties } from './context/PropertyContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { MobileStickyBar } from './components/MobileStickyBar';
import { CookieBanner } from './components/CookieBanner';
import { HomePage } from './pages/HomePage';
import { slugify, getPropertySlug } from './utils/slugify';
import { registerWebMCP } from './utils/webmcp';

// Lazy-loaded routes for optimal Core Web Vitals (LCP, FCP, TBT)
const PropertiesPage = lazy(() => import('./pages/PropertiesPage').then(m => ({ default: m.default || m.PropertiesPage })));
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage').then(m => ({ default: m.default || m.PropertyDetailPage })));
const SellPropertyPage = lazy(() => import('./pages/SellPropertyPage').then(m => ({ default: m.default || m.SellPropertyPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.default || m.AboutPage })));
const GuidePage = lazy(() => import('./pages/GuidePage').then(m => ({ default: m.default || m.GuidePage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.default || m.PrivacyPolicyPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.default || m.TermsPage })));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage').then(m => ({ default: m.default || m.CookiePolicyPage })));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage').then(m => ({ default: m.default || m.AdminDashboardPage })));

const PageLoadingFallback = () => (
  <div className="container" style={{ padding: '60px 20px', minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
    <div className="skeleton" style={{ width: '100%', maxWidth: '800px', height: '220px', borderRadius: '16px' }}></div>
    <div className="skeleton" style={{ width: '60%', height: '24px', borderRadius: '6px' }}></div>
  </div>
);

const MainApp = () => {
  const [currentRoute, setCurrentRoute] = useState('home');
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const { properties } = useProperties();

  // Register WebMCP Context for Browser AI Agents
  useEffect(() => {
    registerWebMCP({
      properties,
      onSelectProperty: handleSelectProperty,
      onNavigate: navigateTo
    });
  }, [properties]);

  // Listen to browser hash and pathname changes for seamless URL navigation
  useEffect(() => {
    const handleRouteCheck = () => {
      const hash = window.location.hash.replace('#', '').replace(/^\/+|\/+$/g, '');
      const path = window.location.pathname.replace(/^\/+|\/+$/g, '');

      const currentPathOrHash = hash || path;

      // Direct check for admin
      if (path === 'admin' || hash === 'admin' || hash.startsWith('admin')) {
        setCurrentRoute('admin');
      } else if (currentPathOrHash.startsWith('propiedad/') || currentPathOrHash.startsWith('propiedades/')) {
        const propSlugOrId = decodeURIComponent(currentPathOrHash.replace(/^(propiedad|propiedades)\//, ''));
        setSelectedPropertyId(propSlugOrId);
        setCurrentRoute('property-detail');
      } else if (currentPathOrHash.startsWith('propiedad-')) {
        const propSlugOrId = decodeURIComponent(currentPathOrHash.replace('propiedad-', ''));
        setSelectedPropertyId(propSlugOrId);
        setCurrentRoute('property-detail');
      } else if (currentPathOrHash === 'propiedades' || currentPathOrHash === 'properties' || currentPathOrHash === 'catalogo') {
        setCurrentRoute('properties');
      } else if (currentPathOrHash === 'vender-o-rentar-propiedad' || currentPathOrHash === 'sell' || currentPathOrHash === 'vende-o-renta') {
        setCurrentRoute('sell');
      } else if (currentPathOrHash === 'guia-inmobiliaria' || currentPathOrHash === 'guide' || currentPathOrHash === 'preguntas-frecuentes' || currentPathOrHash === 'faq') {
        setCurrentRoute('guide');
      } else if (currentPathOrHash === 'nosotros' || currentPathOrHash === 'about' || currentPathOrHash === 'lic-edgar-molina') {
        setCurrentRoute('about');
      } else if (currentPathOrHash === 'aviso-de-privacidad' || currentPathOrHash === 'aviso-privacidad' || currentPathOrHash === 'privacy') {
        setCurrentRoute('privacy');
      } else if (currentPathOrHash === 'terminos-y-condiciones' || currentPathOrHash === 'terminos' || currentPathOrHash === 'terms') {
        setCurrentRoute('terms');
      } else if (currentPathOrHash === 'politica-de-cookies' || currentPathOrHash === 'politica-cookies' || currentPathOrHash === 'cookies') {
        setCurrentRoute('cookies');
      } else if (currentPathOrHash === 'home' || currentPathOrHash === '') {
        setCurrentRoute('home');
      } else {
        setCurrentRoute(hash || 'home');
      }
    };

    window.addEventListener('hashchange', handleRouteCheck);
    window.addEventListener('popstate', handleRouteCheck);
    handleRouteCheck();

    return () => {
      window.removeEventListener('hashchange', handleRouteCheck);
      window.removeEventListener('popstate', handleRouteCheck);
    };
  }, []);

  const navigateTo = (routeId) => {
    setCurrentRoute(routeId);
    if (routeId === 'privacy') {
      window.location.hash = 'aviso-de-privacidad';
    } else if (routeId === 'terms') {
      window.location.hash = 'terminos-y-condiciones';
    } else if (routeId === 'cookies') {
      window.location.hash = 'politica-de-cookies';
    } else {
      window.location.hash = routeId;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectProperty = (propertyOrIdentifier) => {
    let slug = '';
    if (typeof propertyOrIdentifier === 'object' && propertyOrIdentifier !== null) {
      slug = getPropertySlug(propertyOrIdentifier);
    } else {
      const found = properties.find(p => p.id === propertyOrIdentifier || p.slug === propertyOrIdentifier || slugify(p.title) === propertyOrIdentifier);
      slug = found ? getPropertySlug(found) : slugify(propertyOrIdentifier);
    }
    setSelectedPropertyId(slug);
    setCurrentRoute('property-detail');
    window.location.hash = `propiedad/${slug}`;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToProperties = () => {
    navigateTo('properties');
  };

  const currentPropertyObj = properties.find(
    (p) => getPropertySlug(p) === selectedPropertyId || p.slug === selectedPropertyId || String(p.id) === String(selectedPropertyId) || slugify(p.title) === selectedPropertyId
  );

  // If in admin route (direct link), render dedicated Admin CMS view
  if (currentRoute === 'admin') {
    return (
      <div className="app-container" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Suspense fallback={<PageLoadingFallback />}>
          <AdminDashboardPage />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="app-container" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Fixed Institutional Header */}
      <Navbar activeRoute={currentRoute} onNavigate={navigateTo} />

      {/* Main Routed Content */}
      <main style={{ flex: 1 }}>
        {currentRoute === 'home' && (
          <HomePage
            onNavigate={navigateTo}
            onSelectProperty={handleSelectProperty}
          />
        )}

        <Suspense fallback={<PageLoadingFallback />}>
          {currentRoute === 'properties' && (
            <PropertiesPage
              onSelectProperty={handleSelectProperty}
            />
          )}

          {currentRoute === 'property-detail' && (
            <PropertyDetailPage
              propertyId={selectedPropertyId}
              onBack={handleBackToProperties}
              onSelectProperty={handleSelectProperty}
            />
          )}

          {currentRoute === 'sell' && <SellPropertyPage />}

          {currentRoute === 'about' && <AboutPage onNavigate={navigateTo} />}

          {currentRoute === 'guide' && <GuidePage />}

          {currentRoute === 'privacy' && <PrivacyPolicyPage onNavigate={navigateTo} />}

          {currentRoute === 'terms' && <TermsPage onNavigate={navigateTo} />}

          {currentRoute === 'cookies' && <CookiePolicyPage onNavigate={navigateTo} />}
        </Suspense>
      </main>

      {/* Institutional Footer */}
      <Footer onNavigate={navigateTo} />

      {/* Cookie Consent Banner & Preferences Manager */}
      <CookieBanner onNavigate={navigateTo} />

      {/* Sticky Bottom Action Bar for Mobile Conversions */}
      <MobileStickyBar currentProperty={currentRoute === 'property-detail' ? currentPropertyObj : null} />
    </div>
  );
};

export function App() {
  return (
    <PropertyProvider>
      <MainApp />
    </PropertyProvider>
  );
}

export default App;
